import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PaperPlaneTilt, ArrowClockwise, FloppyDisk } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [allSymbols, setAllSymbols] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetBalance, setResetBalance] = useState("1000");

  useEffect(() => {
    endpoints.settings().then((r) => setS(r.data));
    endpoints.symbols(100).then((r) => setAllSymbols(r.data.symbols || []));
  }, []);

  if (!s) return <div className="p-8 text-muted-foreground">Loading...</div>;

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await endpoints.updateSettings(s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testTelegram = async () => {
    if (!s.telegram_bot_token || !s.telegram_chat_id) {
      toast.error("Fill bot token & chat ID first");
      return;
    }
    try {
      const { data } = await endpoints.telegramTest(s.telegram_bot_token, s.telegram_chat_id);
      if (data.ok) toast.success(`Telegram test sent! (${data.sent}/${data.total} akun)`);
      else toast.error(data.description || data.error || "Telegram failed");
    } catch (e) {
      toast.error("Telegram request failed");
    }
  };

  const testBinance = async () => {
    if (!s.binance_api_key || !s.binance_api_secret) {
      toast.error("Fill Binance API key & secret first");
      return;
    }
    try {
      const { data } = await endpoints.binanceTest(
        s.binance_api_key,
        s.binance_api_secret,
        s.binance_testnet,
      );
      if (data.ok) {
        toast.success(
          `Connected · Balance $${(data.wallet_balance ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} (${data.testnet ? "TESTNET" : "MAINNET"})`,
        );
      } else if (data.error === "geo_blocked") {
        toast.error(
          "Server is geo-blocked by Binance (451). Try TESTNET or run from allowed region.",
        );
      } else {
        toast.error(data.detail || data.error || "Binance test failed");
      }
    } catch (e) {
      toast.error("Binance test request failed");
    }
  };

  const toggleWatched = (sym) => {
    const has = s.watched_symbols.includes(sym);
    set("watched_symbols", has ? s.watched_symbols.filter((x) => x !== sym) : [...s.watched_symbols, sym]);
  };

  const resetWallet = async () => {
    const amt = parseFloat(resetBalance);
    if (Number.isNaN(amt) || amt <= 0) return toast.error("Invalid balance");
    if (!window.confirm(`Reset wallet to $${amt} and clear all trades?`)) return;
    try {
      await endpoints.resetPortfolio(amt);
      toast.success("Portfolio reset");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar />
      <main className="flex-1 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Configuration
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Bot Settings</h1>
          </div>
          <Button
            data-testid="save-settings-btn"
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-signal-brand hover:bg-signal-brand/90"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        {/* Strategy Preset */}
        <div className="border border-border rounded-sm bg-[#0d0d0d] p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-bold text-lg">Strategy Preset</h2>
              <div className="text-xs text-muted-foreground">
                Switch tuning for scalping (fast &amp; tight) or swing (patient &amp; wide).
              </div>
            </div>
            <span
              className={`font-mono text-[10px] uppercase px-2 py-1 rounded-sm border ${
                s.strategy === "scalping"
                  ? "border-signal-buy/40 text-signal-buy bg-signal-buy/10"
                  : "border-signal-brand/40 text-signal-brand bg-signal-brand/10"
              }`}
            >
              {(s.strategy || "swing").toUpperCase()}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              data-testid="preset-scalping-btn"
              onClick={() => {
                setS((prev) => ({
                  ...prev,
                  strategy: "scalping",
                  kline_interval: "1m",
                  tick_seconds: 6,
                  leverage: 20,
                  tp_pct: 0.5,
                  sl_pct: 0.3,
                  risk_per_trade_pct: 3,
                }));
                toast.success("Scalping preset loaded — remember to Save");
              }}
              className={`text-left border rounded-sm p-4 transition-colors ${
                s.strategy === "scalping"
                  ? "border-signal-buy bg-signal-buy/10"
                  : "border-border hover:bg-[#141414]"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-signal-buy" />
                <div className="font-heading font-black text-base">SCALPING</div>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground space-y-1">
                <div>Timeframe: 1m · Tick: 6s</div>
                <div>TP: 0.5% · SL: 0.3% · Lev: 20x</div>
              </div>
              <div className="text-[11px] mt-2">
                Fast micro-trades. Sinyal entry 100% dari AI pilihan Anda.
              </div>
            </button>
            <button
              data-testid="preset-swing-btn"
              onClick={() => {
                setS((prev) => ({
                  ...prev,
                  strategy: "swing",
                  kline_interval: "15m",
                  tick_seconds: 20,
                  leverage: 10,
                  tp_pct: 2,
                  sl_pct: 1,
                  risk_per_trade_pct: 5,
                }));
                toast.success("Swing preset loaded — remember to Save");
              }}
              className={`text-left border rounded-sm p-4 transition-colors ${
                s.strategy === "swing"
                  ? "border-signal-brand bg-signal-brand/10"
                  : "border-border hover:bg-[#141414]"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-signal-brand" />
                <div className="font-heading font-black text-base">SWING</div>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground space-y-1">
                <div>Timeframe: 15m · Tick: 20s</div>
                <div>TP: 2% · SL: 1% · Lev: 10x</div>
              </div>
              <div className="text-[11px] mt-2">
                Longer holds. Sinyal entry 100% dari AI pilihan Anda.
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Trading params */}
          <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-5">
            <h2 className="font-heading font-bold text-lg">Trading Parameters</h2>

            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Mode
              </Label>
              <Select value={s.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="mode-select" className="rounded-sm mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paper">Paper Trading</SelectItem>
                  <SelectItem value="live" disabled={!s.binance_api_key || !s.binance_api_secret}>
                    Live Trading{" "}
                    {(!s.binance_api_key || !s.binance_api_secret) && "(needs API keys)"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <SliderField
              testid="leverage-slider"
              label="Leverage"
              value={s.leverage}
              min={1}
              max={125}
              step={1}
              suffix="x"
              onChange={(v) => set("leverage", v)}
            />
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Mode TP / SL
              </Label>
              <Select
                value={s.atr_tp_sl_enabled !== false ? "atr" : "static"}
                onValueChange={(v) => set("atr_tp_sl_enabled", v === "atr")}
              >
                <SelectTrigger data-testid="tpsl-mode-select" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atr">ATR Dynamic — TP/SL ikut volatilitas koin (rekomendasi)</SelectItem>
                  <SelectItem value="static">Statis % — TP/SL persentase tetap</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground">
                {s.atr_tp_sl_enabled !== false
                  ? "Aktif: ATR Dynamic. TP/SL dihitung dari volatilitas tiap koin — setting statis % diabaikan."
                  : "Aktif: Statis %. TP/SL persentase tetap untuk semua koin — ATR diabaikan."}
              </div>
            </div>
            {s.atr_tp_sl_enabled !== false ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-mono text-[10px] uppercase text-signal-sell">SL × ATR</Label>
                  <Input data-testid="atr-sl-mult-input" className="rounded-sm mt-1 font-mono" type="number" step="0.1" min="0.5"
                    value={s.atr_sl_mult ?? 1.5}
                    onChange={(e) => set("atr_sl_mult", parseFloat(e.target.value) || 1.5)} />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase text-signal-buy">TP × ATR</Label>
                  <Input data-testid="atr-tp-mult-input" className="rounded-sm mt-1 font-mono" type="number" step="0.1" min="0.5"
                    value={s.atr_tp_mult ?? 2.5}
                    onChange={(e) => set("atr_tp_mult", parseFloat(e.target.value) || 2.5)} />
                </div>
              </div>
            ) : (
              <>
                <SliderField
                  testid="tp-slider"
                  label="Take Profit"
                  value={s.tp_pct}
                  min={0.2}
                  max={20}
                  step={0.1}
                  suffix="%"
                  onChange={(v) => set("tp_pct", v)}
                  color="text-signal-buy"
                />
                <SliderField
                  testid="sl-slider"
                  label="Stop Loss"
                  value={s.sl_pct}
                  min={0.2}
                  max={10}
                  step={0.1}
                  suffix="%"
                  onChange={(v) => set("sl_pct", v)}
                  color="text-signal-sell"
                />
              </>
            )}
            <SliderField
              testid="risk-slider"
              label="Risk per Trade (% of wallet as margin)"
              value={s.risk_per_trade_pct}
              min={1}
              max={50}
              step={1}
              suffix="%"
              onChange={(v) => set("risk_per_trade_pct", v)}
            />
            <SliderField
              testid="maxpos-slider"
              label="Max Concurrent Positions"
              value={s.max_positions ?? 20}
              min={1}
              max={20}
              step={1}
              suffix=""
              onChange={(v) => set("max_positions", v)}
            />

            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Partial Take-Profit
                  </Label>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Close sebagian posisi saat profit trigger (lock gain), sisanya di-BE.
                  </div>
                </div>
                <Switch
                  data-testid="partial-tp-switch"
                  checked={!!s.partial_tp_enabled}
                  onCheckedChange={(v) => set("partial_tp_enabled", v)}
                />
              </div>
              {s.partial_tp_enabled && (
                <>
                  <SliderField
                    testid="ptp-trigger-slider"
                    label="Partial TP Trigger (% profit)"
                    value={s.partial_tp_trigger_pct}
                    min={0.1}
                    max={3}
                    step={0.05}
                    suffix="%"
                    color="text-signal-buy"
                    onChange={(v) => set("partial_tp_trigger_pct", v)}
                  />
                  <SliderField
                    testid="ptp-ratio-slider"
                    label="Portion to Close"
                    value={Math.round((s.partial_tp_close_ratio || 0.5) * 100)}
                    min={10}
                    max={90}
                    step={5}
                    suffix="%"
                    onChange={(v) => set("partial_tp_close_ratio", v / 100)}
                  />
                </>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Break-even Trailing SL
                  </Label>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Auto-lock SL ke entry saat profit trigger. Otomatis aktif setelah Partial TP.
                  </div>
                </div>
                <Switch
                  data-testid="trailing-be-switch"
                  checked={!!s.trailing_be_enabled}
                  onCheckedChange={(v) => set("trailing_be_enabled", v)}
                />
              </div>
              {s.trailing_be_enabled && (
                <>
                  {s.atr_tp_sl_enabled !== false ? (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Label className="font-mono text-[10px] uppercase text-signal-buy">BE Trigger × ATR</Label>
                        <span data-testid="be-atr-badge" className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-signal-buy/10 text-signal-buy border border-signal-buy/30">
                          Auto: {s.atr_be_trigger_mult ?? 0.8}×ATR
                        </span>
                      </div>
                      <Input data-testid="atr-be-trigger-mult-input" className="rounded-sm font-mono" type="number" step="0.1" min="0.1"
                        value={s.atr_be_trigger_mult ?? 0.8}
                        onChange={(e) => set("atr_be_trigger_mult", parseFloat(e.target.value) || 0.8)} />
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Mode ATR aktif: trigger BE = {s.atr_be_trigger_mult ?? 0.8} × ATR tiap koin — slider % statis diabaikan.
                      </div>
                    </div>
                  ) : (
                    <SliderField
                      testid="be-trigger-slider"
                      label="BE Trigger (% profit dari entry)"
                      value={s.trailing_be_trigger_pct}
                      min={0.1}
                      max={2}
                      step={0.05}
                      suffix="%"
                      color="text-signal-buy"
                      onChange={(v) => set("trailing_be_trigger_pct", v)}
                    />
                  )}
                  <SliderField
                    testid="be-lock-slider"
                    label="BE Lock Offset (% profit dikunci)"
                    value={s.trailing_be_lock_pct ?? 0.1}
                    min={0}
                    max={1}
                    step={0.05}
                    suffix="%"
                    color="text-signal-buy"
                    onChange={(v) => set("trailing_be_lock_pct", v)}
                  />
                  <div className="text-[11px] text-muted-foreground">
                    Saat trigger kena, SL dikunci di entry + offset ini — close BE tetap
                    profit kecil &amp; tahan slippage. Set 0 = kunci persis di entry (hasil bisa nol/minus tipis).
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Trailing Stop Dinamis
                  </Label>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Setelah BE terkunci, SL ikut mengejar harga puncak — profit terkunci makin besar saat harga terus naik.
                  </div>
                </div>
                <Switch
                  data-testid="trailing-stop-switch"
                  checked={!!s.trailing_stop_enabled}
                  onCheckedChange={(v) => set("trailing_stop_enabled", v)}
                />
              </div>
              {s.trailing_stop_enabled && (
                s.atr_tp_sl_enabled !== false ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Label className="font-mono text-[10px] uppercase text-signal-buy">Jarak Trail × ATR</Label>
                      <span data-testid="trail-atr-badge" className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-signal-buy/10 text-signal-buy border border-signal-buy/30">
                        Auto: {s.atr_trailing_mult ?? 1.0}×ATR
                      </span>
                    </div>
                    <Input data-testid="atr-trailing-mult-input" className="rounded-sm font-mono" type="number" step="0.1" min="0.1"
                      value={s.atr_trailing_mult ?? 1.0}
                      onChange={(e) => set("atr_trailing_mult", parseFloat(e.target.value) || 1.0)} />
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Mode ATR aktif: jarak trail = {s.atr_trailing_mult ?? 1.0} × ATR tiap koin — slider % statis diabaikan.
                    </div>
                  </div>
                ) : (
                  <SliderField
                    testid="trailing-stop-distance-slider"
                    label="Jarak Trail (% di bawah harga puncak)"
                    value={s.trailing_stop_distance_pct ?? 0.2}
                    min={0.05}
                    max={2}
                    step={0.05}
                    suffix="%"
                    color="text-signal-buy"
                    onChange={(v) => set("trailing_stop_distance_pct", v)}
                  />
                )
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block">
                Daily Profit Target (USDT)
              </Label>
              <div className="text-[11px] text-muted-foreground">
                Bot auto-OFF + force close semua posisi saat realized PnL harian tercapai. Set 0 untuk disable.
              </div>
              <Input
                data-testid="daily-target-input"
                className="rounded-sm font-mono"
                type="number"
                step="0.5"
                min="0"
                value={s.daily_profit_target_usdt ?? 0}
                onChange={(e) => set("daily_profit_target_usdt", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 25 (=$25 USDT/hari)"
              />
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Saat Target Tercapai
                </Label>
                <Select
                  value={s.daily_target_action || "off_and_close"}
                  onValueChange={(v) => set("daily_target_action", v)}
                >
                  <SelectTrigger data-testid="daily-target-action-select" className="rounded-sm mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off_and_close">
                      Force close all + Bot OFF (recommended)
                    </SelectItem>
                    <SelectItem value="notify_only">Notify only (bot tetap jalan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-signal-sell block">
                Daily Loss Limit (USDT)
              </Label>
              <div className="text-[11px] text-muted-foreground">
                Proteksi risiko — bot auto-OFF + force close saat loss harian kena batas. Isi angka positif (misal 30 = stop di -$30). Set 0 untuk disable.
              </div>
              <Input
                data-testid="daily-loss-input"
                className="rounded-sm font-mono border-signal-sell/30 focus-visible:ring-signal-sell/40"
                type="number"
                step="0.5"
                min="0"
                value={s.daily_loss_limit_usdt ?? 0}
                onChange={(e) => set("daily_loss_limit_usdt", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 30 (=stop di -$30 USDT/hari)"
              />
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Saat Loss Limit Tercapai
                </Label>
                <Select
                  value={s.daily_loss_action || "off_and_close"}
                  onValueChange={(v) => set("daily_loss_action", v)}
                >
                  <SelectTrigger data-testid="daily-loss-action-select" className="rounded-sm mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off_and_close">
                      Force close all + Bot OFF (recommended)
                    </SelectItem>
                    <SelectItem value="notify_only">Notify only (bot tetap jalan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-signal-buy block">
                Basket Take Profit — Close All (USDT)
              </Label>
              <div className="text-[11px] text-muted-foreground">
                Jika <b>total profit gabungan semua posisi terbuka</b> mencapai nilai ini, bot
                otomatis close SEMUA posisi sekaligus. Bot tetap ON &amp; lanjut cari entry baru.
                Set 0 untuk disable.
              </div>
              <Input
                data-testid="basket-tp-input"
                className="rounded-sm font-mono border-signal-buy/30 focus-visible:ring-signal-buy/40"
                type="number"
                step="0.5"
                min="0"
                value={s.basket_tp_usdt ?? 0}
                onChange={(e) => set("basket_tp_usdt", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 10 (=close all saat total open PnL +$10)"
              />
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Jeda Setelah Basket TP (menit)
                </Label>
                <div className="text-[11px] text-muted-foreground mt-1 mb-2">
                  Setelah Basket TP close all, bot menunggu selama ini sebelum scan &amp; entry
                  koin lagi. 0 = tanpa jeda.
                </div>
                <Input
                  data-testid="basket-tp-pause-input"
                  className="rounded-sm font-mono"
                  type="number"
                  step="1"
                  min="0"
                  value={s.basket_tp_pause_minutes ?? 5}
                  onChange={(e) => set("basket_tp_pause_minutes", parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-signal-sell block">
                Basket Take Loss — Close All (USDT)
              </Label>
              <div className="text-[11px] text-muted-foreground">
                Jika <b>total loss gabungan semua posisi terbuka</b> mencapai nilai ini (minus),
                bot otomatis close SEMUA posisi sekaligus. Set 0 untuk disable.
              </div>
              <Input
                data-testid="basket-sl-input"
                className="rounded-sm font-mono border-signal-sell/30 focus-visible:ring-signal-sell/40"
                type="number"
                step="0.5"
                min="0"
                value={s.basket_sl_usdt ?? 0}
                onChange={(e) => set("basket_sl_usdt", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 20 (=close all saat total open PnL -$20)"
              />
              {(s.basket_sl_usdt ?? 0) > 0 && (
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Setelah Basket Loss Tercapai
                  </Label>
                  <Select
                    value={s.basket_sl_action || "continue"}
                    onValueChange={(v) => set("basket_sl_action", v)}
                  >
                    <SelectTrigger data-testid="basket-sl-action-select" className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continue">Bot tetap ON — lanjut cari entry baru</SelectItem>
                      <SelectItem value="stop">Bot STOP — matikan sampai dinyalakan manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <SliderField
                testid="min-avail-slider"
                label="Min Available / Equity (buat entry baru)"
                value={s.min_available_ratio_pct}
                min={0}
                max={90}
                step={5}
                suffix="%"
                onChange={(v) => set("min_available_ratio_pct", v)}
              />
              <div className="text-[11px] text-muted-foreground">
                Bot tidak akan open trade baru kalau saldo available &lt; % ini dari equity total.
                Set 0 untuk disable guard.
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Balance Report ke Telegram
                  </Label>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Kirim snapshot saldo periodik ke Telegram.
                  </div>
                </div>
                <Switch
                  data-testid="balance-report-switch"
                  checked={!!s.balance_report_enabled}
                  onCheckedChange={(v) => set("balance_report_enabled", v)}
                />
              </div>
              {s.balance_report_enabled && (
                <SliderField
                  testid="balance-report-slider"
                  label="Interval Report"
                  value={s.balance_report_minutes}
                  min={5}
                  max={120}
                  step={5}
                  suffix=" menit"
                  onChange={(v) => set("balance_report_minutes", v)}
                />
              )}
            </div>
          </div>

          {/* Telegram + Binance Keys + Reset */}
          <div className="space-y-4">
            {/* Signal Source Toggle */}
            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-4">
              <h2 className="font-heading font-bold text-lg">Sumber Sinyal Entry</h2>
              <div className="text-xs text-muted-foreground">
                Pilih SATU AI sebagai sumber sinyal entry bot. Tidak bisa aktif bersamaan —
                bot hanya mengikuti rekomendasi dari sumber yang dipilih.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="signal-source-neurobro-btn"
                  onClick={() => set("signal_source", "neurobro")}
                  className={`text-left border rounded-sm p-4 transition-colors ${
                    (s.signal_source || "neurobro") === "neurobro"
                      ? "border-signal-buy bg-signal-buy/10"
                      : "border-border hover:bg-[#141414]"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${(s.signal_source || "neurobro") === "neurobro" ? "bg-signal-buy" : "bg-muted"}`} />
                    <div className="font-heading font-black text-sm">NEUROBRO AI</div>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    Crypto market intelligence khusus trading
                  </div>
                </button>
                <button
                  data-testid="signal-source-claude-btn"
                  onClick={() => set("signal_source", "claude")}
                  className={`text-left border rounded-sm p-4 transition-colors ${
                    s.signal_source === "claude"
                      ? "border-signal-brand bg-signal-brand/10"
                      : "border-border hover:bg-[#141414]"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${s.signal_source === "claude" ? "bg-signal-brand" : "bg-muted"}`} />
                    <div className="font-heading font-black text-sm">CLAUDE AI</div>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    Sinyal langsung dari Claude Fable 5 (Anthropic)
                  </div>
                </button>
              </div>
              {s.signal_source === "claude" && !s.claude_api_key && (
                <div className="text-[11px] font-mono text-signal-warn" data-testid="claude-key-warning">
                  ⚠ Isi Claude API Key di bawah agar bot bisa scan entry.
                </div>
              )}
              {(s.signal_source || "neurobro") === "neurobro" && !s.neurobro_api_key && (
                <div className="text-[11px] font-mono text-signal-warn" data-testid="neurobro-key-warning">
                  ⚠ Isi Neurobro API Key di bawah agar bot bisa scan entry.
                </div>
              )}
            </div>

            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-lg">Neurobro AI</h2>
                <span
                  data-testid="neurobro-status-badge"
                  className={`font-mono text-[10px] uppercase px-2 py-1 rounded-sm border ${
                    (s.signal_source || "neurobro") === "neurobro" && s.neurobro_api_key
                      ? "border-signal-buy/40 text-signal-buy bg-signal-buy/10"
                      : "border-border text-muted-foreground bg-muted/10"
                  }`}
                >
                  {(s.signal_source || "neurobro") === "neurobro"
                    ? (s.neurobro_api_key ? "SUMBER AKTIF" : "NO API KEY")
                    : "STANDBY"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Purpose-built crypto trading AI (Managed NeuroAPI). Bot buka posisi
                mengikuti rekomendasi bullish (LONG) / bearish (SHORT) saat dipilih
                sebagai sumber sinyal. Get key at{" "}
                <a
                  href="https://neuroapi.neurobro.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="text-signal-brand underline"
                >
                  neuroapi.neurobro.ai
                </a>
                .
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Neurobro API Key
                </Label>
                <Input
                  data-testid="neurobro-key-input"
                  className="rounded-sm mt-2 font-mono"
                  type="password"
                  value={s.neurobro_api_key || ""}
                  onChange={(e) => set("neurobro_api_key", e.target.value)}
                  placeholder="Paste your Neurobro API key"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Intelligence Mode
                </Label>
                <Select
                  value={s.neurobro_mode || "Smart"}
                  onValueChange={(v) => set("neurobro_mode", v)}
                >
                  <SelectTrigger
                    data-testid="neurobro-mode-select"
                    className="rounded-sm mt-2"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fast">Fast — quick, cheap</SelectItem>
                    <SelectItem value="Smart">Smart — balanced (recommended)</SelectItem>
                    <SelectItem value="Max">Max — deepest reasoning, most cost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-[10px] text-signal-warn font-mono">
                Cache: 5 min/symbol · Bot entry murni ikut sinyal AI (tanpa filter teknikal)
              </div>
            </div>

            {/* Claude AI */}
            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-lg">Claude AI</h2>
                <span
                  data-testid="claude-status-badge"
                  className={`font-mono text-[10px] uppercase px-2 py-1 rounded-sm border ${
                    s.signal_source === "claude" && s.claude_api_key
                      ? "border-signal-brand/40 text-signal-brand bg-signal-brand/10"
                      : "border-border text-muted-foreground bg-muted/10"
                  }`}
                >
                  {s.signal_source === "claude"
                    ? (s.claude_api_key ? "SUMBER AKTIF" : "NO API KEY")
                    : "STANDBY"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Sinyal entry langsung dari <span className="text-white font-bold">Claude Fable 5</span> (Anthropic).
                Claude menganalisa data harga candlestick real-time Binance lalu memutuskan
                LONG / SHORT / SKIP dengan confidence — tanpa indikator teknikal. Get key at{" "}
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noreferrer"
                  className="text-signal-brand underline"
                >
                  console.anthropic.com
                </a>
                .
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Anthropic API Key
                </Label>
                <Input
                  data-testid="claude-key-input"
                  className="rounded-sm mt-2 font-mono"
                  type="password"
                  value={s.claude_api_key || ""}
                  onChange={(e) => set("claude_api_key", e.target.value)}
                  placeholder="sk-ant-..."
                />
              </div>
              <div className="text-[10px] text-signal-warn font-mono">
                Model: claude-fable-5 · Cache: 5 min/symbol · Biaya API ditagih ke akun Anthropic Anda
              </div>
            </div>

            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-lg">Binance API Keys</h2>
                <span
                  className={`font-mono text-[10px] uppercase px-2 py-1 rounded-sm border ${
                    s.binance_testnet
                      ? "border-signal-warn/40 text-signal-warn bg-signal-warn/10"
                      : "border-signal-sell/40 text-signal-sell bg-signal-sell/10"
                  }`}
                >
                  {s.binance_testnet ? "TESTNET" : "MAINNET"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                For LIVE trading. Get keys from{" "}
                <a
                  href={s.binance_testnet ? "https://testnet.binancefuture.com/" : "https://www.binance.com/en/my/settings/api-management"}
                  target="_blank"
                  rel="noreferrer"
                  className="text-signal-brand underline"
                >
                  {s.binance_testnet ? "testnet.binancefuture.com" : "Binance API Management"}
                </a>
                . Enable Futures permission. Keys are stored in your local DB only.
              </div>
              <div className="flex items-center justify-between gap-2 py-2 border-y border-border">
                <div>
                  <div className="font-mono text-[11px] uppercase">Use Testnet</div>
                  <div className="text-[10px] text-muted-foreground">
                    Recommended — safe fake money, same API surface
                  </div>
                </div>
                <Switch
                  data-testid="binance-testnet-switch"
                  checked={!!s.binance_testnet}
                  onCheckedChange={(v) => set("binance_testnet", v)}
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  API Key
                </Label>
                <Input
                  data-testid="binance-apikey-input"
                  className="rounded-sm mt-2 font-mono"
                  type="password"
                  value={s.binance_api_key || ""}
                  onChange={(e) => set("binance_api_key", e.target.value)}
                  placeholder="Enter your Binance API key"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  API Secret
                </Label>
                <Input
                  data-testid="binance-apisecret-input"
                  className="rounded-sm mt-2 font-mono"
                  type="password"
                  value={s.binance_api_secret || ""}
                  onChange={(e) => set("binance_api_secret", e.target.value)}
                  placeholder="Enter your Binance API secret"
                />
              </div>
              <Button
                data-testid="binance-test-btn"
                onClick={testBinance}
                variant="outline"
                className="rounded-sm border-signal-brand/40 text-signal-brand hover:bg-signal-brand/10 hover:text-signal-brand"
              >
                <PaperPlaneTilt size={14} className="mr-2" />
                Test Binance Connection
              </Button>
              <div className="text-[10px] text-signal-warn font-mono">
                ⚠ Container region may be blocked by Binance mainnet (HTTP 451). Testnet usually works.
              </div>
            </div>

            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-4">
              <h2 className="font-heading font-bold text-lg">Telegram Notifications</h2>
              <div className="text-xs text-muted-foreground">
                Get instant trade alerts. Create a bot via{" "}
                <a
                  href="https://t.me/BotFather"
                  target="_blank"
                  rel="noreferrer"
                  className="text-signal-brand underline"
                >
                  @BotFather
                </a>{" "}
                to get your token. Get your chat ID from{" "}
                <a
                  href="https://t.me/userinfobot"
                  target="_blank"
                  rel="noreferrer"
                  className="text-signal-brand underline"
                >
                  @userinfobot
                </a>
                .
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Bot Token
                </Label>
                <Input
                  data-testid="telegram-token-input"
                  className="rounded-sm mt-2 font-mono"
                  type="password"
                  value={s.telegram_bot_token || ""}
                  onChange={(e) => set("telegram_bot_token", e.target.value)}
                  placeholder="123456:ABC-DEF..."
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Chat ID (bisa lebih dari 1)
                </Label>
                <Input
                  data-testid="telegram-chatid-input"
                  className="rounded-sm mt-2 font-mono"
                  value={s.telegram_chat_id || ""}
                  onChange={(e) => set("telegram_chat_id", e.target.value)}
                  placeholder="123456789, 987654321, 555444333"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Pisahkan dengan koma untuk kirim ke beberapa akun (misal 3 akun).
                  Setiap akun harus sudah chat /start ke bot yang sama.
                </div>
              </div>
              <Button
                data-testid="telegram-test-btn"
                onClick={testTelegram}
                variant="outline"
                className="rounded-sm"
              >
                <PaperPlaneTilt size={14} className="mr-2" />
                Send Test Message
              </Button>
            </div>

            <div className="border border-border rounded-sm bg-[#0d0d0d] p-5 space-y-3">
              <h2 className="font-heading font-bold text-lg">Paper Wallet</h2>
              <div className="text-xs text-muted-foreground">
                Reset your simulated wallet. This will close & delete ALL trades.
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="reset-balance-input"
                  className="rounded-sm font-mono"
                  value={resetBalance}
                  onChange={(e) => setResetBalance(e.target.value)}
                  placeholder="1000"
                />
                <Button
                  data-testid="reset-wallet-btn"
                  onClick={resetWallet}
                  variant="outline"
                  className="rounded-sm border-signal-warn/40 text-signal-warn hover:bg-signal-warn/10 hover:text-signal-warn"
                >
                  <ArrowClockwise size={14} className="mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-border rounded-sm bg-[#0d0d0d] p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-lg">Watched Coins</h2>
            <div className="font-mono text-[11px] text-muted-foreground">
              {s.watched_symbols.length} / {allSymbols.length} selected
            </div>
          </div>
          <div className="text-xs text-muted-foreground mb-4">
            Bot will scan these pairs every 20s and open positions when signals meet the threshold.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[420px] overflow-y-auto pr-2">
            {allSymbols.map((sym) => {
              const active = s.watched_symbols.includes(sym.symbol);
              return (
                <button
                  key={sym.symbol}
                  data-testid={`watch-${sym.symbol}`}
                  onClick={() => toggleWatched(sym.symbol)}
                  className={`flex items-center justify-between border px-3 py-2 rounded-sm text-left transition-colors ${
                    active
                      ? "border-signal-brand bg-signal-brand/10 text-white"
                      : "border-border hover:bg-[#141414]"
                  }`}
                >
                  <span className="font-heading text-sm">{sym.symbol.replace("USDT", "")}</span>
                  <span
                    className={`font-mono text-[10px] ${
                      sym.price_change_pct >= 0 ? "text-signal-buy" : "text-signal-sell"
                    }`}
                  >
                    {sym.price_change_pct >= 0 ? "+" : ""}
                    {sym.price_change_pct.toFixed(1)}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}

const SliderField = ({ label, value, min, max, step, suffix = "", onChange, testid, color }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <span className={`font-mono text-sm font-bold ${color || "text-white"}`} data-testid={`${testid}-value`}>
        {value}
        {suffix}
      </span>
    </div>
    <Slider
      data-testid={testid}
      value={[Number(value)]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
    />
  </div>
);
